import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthHeroPanel } from "../components/AuthHeroPanel";

export default function EmergencyAccessPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/breakglass/login", form);
      completeLogin(null, data.user);
      toast.warning(data.drill
        ? "Drill passed. Your 5-minute practice session is view-only."
        : "Emergency session started. It ends automatically in 15 minutes.");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setForm((current) => ({ ...current, password: "" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" style={{ background: "#14110C" }} data-testid="emergency-access-page">
      <AuthHeroPanel testId="emergency-access-panel" logoTestId="emergency-access-brand"
        footer={<><LifeBuoy size={14} /> Emergency administrator access</>} />
      <div className="col-span-1 lg:col-span-3 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md border p-8 sm:p-10" style={{ background: "#1E1A13", borderColor: "rgba(232,168,56,0.35)" }}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] mb-6" style={{ color: "#E8A838" }}>
          <LifeBuoy size={14} /> Emergency administrator access
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl leading-tight mb-3" style={{ color: "#F7F1E6" }}>
          Breakglass sign-in
        </h1>
        <p className="text-sm mb-8" style={{ color: "#B9AE9A" }}>
          Only for dedicated emergency identities while Microsoft 365 sign-in is unavailable and
          emergency access has been switched on, or during a practice window an administrator has
          opened. Sessions are short and every attempt is recorded.
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="emergency-access-form">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "#B9AE9A" }}>Emergency username</span>
            <input className="w-full bg-transparent border px-3 py-2.5 text-sm outline-none focus:border-[#E8A838] transition-colors duration-150" style={{ borderColor: "rgba(247,241,230,0.2)", color: "#F7F1E6" }}
              autoComplete="username" value={form.username} required
              onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="emergency-username" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "#B9AE9A" }}>Password</span>
            <input type="password" className="w-full bg-transparent border px-3 py-2.5 text-sm outline-none focus:border-[#E8A838] transition-colors duration-150" style={{ borderColor: "rgba(247,241,230,0.2)", color: "#F7F1E6" }}
              autoComplete="current-password" value={form.password} required
              onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emergency-password" />
          </label>
          {error && <p className="text-sm" style={{ color: "#F2A58E" }} role="alert" data-testid="emergency-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-transform duration-150 hover:-translate-y-px disabled:opacity-50" style={{ background: "#E8A838", color: "#14110C" }} data-testid="emergency-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Start emergency session"}
          </button>
        </form>
        <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-xs no-underline hover:underline" style={{ color: "#B9AE9A" }} data-testid="emergency-back-to-login">
          <ArrowLeft size={12} /> Back to normal sign-in
        </Link>
      </div>
      </div>
    </div>
  );
}
